import React, { useState } from "react";
import { useAuth } from "../../contexts/authContext";
import { useNavigate } from "react-router-dom";
import { fetchRegisterUser } from "../../services/api";
import { useLanguage } from "../../contexts/LanguageContext";
import { translations } from "./translations";

const RegisterPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const { currentLanguage } = useLanguage();
  const t = translations[currentLanguage] || translations.ru;

  const [formData, setFormData] = useState({
    lastName: "",
    firstName: "",
    middleName: "",
    gender: "not_specified",
    birthYear: "",
    nationality: "",
    address: "",
    education: "secondary",
    socialStatus: "student",
    workplace: "",
    academicDegree: "",
    studyPlace: "",
    email: "",
    password: "",
    image: null,
  });
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const genderOptions = [
    { value: "male", label: t.register.options.gender.male },
    { value: "female", label: t.register.options.gender.female },
    { value: "other", label: t.register.options.gender.other },
    { value: "not_specified", label: t.register.options.gender.not_specified },
  ];

  const nationalityOptions = [
    t.register.options.nationality.kazakh,
    t.register.options.nationality.russian,
    t.register.options.nationality.ukrainian,
    t.register.options.nationality.uzbek,
    t.register.options.nationality.tatar,
    t.register.options.nationality.german,
    t.register.options.nationality.korean,
    t.register.options.nationality.turkish,
    t.register.options.nationality.other,
  ];

  const educationOptions = [
    { value: "elementary", label: t.register.options.education.elementary },
    { value: "secondary", label: t.register.options.education.secondary },
    { value: "vocational", label: t.register.options.education.vocational },
    { value: "higher", label: t.register.options.education.higher },
    {
      value: "academic_degree",
      label: t.register.options.education.academic_degree,
    },
  ];

  const socialStatusOptions = [
    { value: "student", label: t.register.options.socialStatus.student },
    { value: "employed", label: t.register.options.socialStatus.employed },
    { value: "unemployed", label: t.register.options.socialStatus.unemployed },
    { value: "pensioner", label: t.register.options.socialStatus.pensioner },
    {
      value: "school_student",
      label: t.register.options.socialStatus.school_student,
    },
    {
      value: "entrepreneur",
      label: t.register.options.socialStatus.entrepreneur,
    },
    {
      value: "civil_servant",
      label: t.register.options.socialStatus.civil_servant,
    },
    { value: "other", label: t.register.options.socialStatus.other },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    // Очищаем ошибку поля при изменении
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.lastName.trim()) {
      newErrors.lastName = t.register.errors.lastName;
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = t.register.errors.firstName;
    }

    if (!formData.birthYear) {
      newErrors.birthYear = t.register.errors.birthYear.required;
    } else {
      const year = parseInt(formData.birthYear);
      const currentYear = new Date().getFullYear();
      if (year < 1900 || year > currentYear) {
        newErrors.birthYear = t.register.errors.birthYear.invalid;
      }
    }

    if (!formData.address.trim()) {
      newErrors.address = t.register.errors.address;
    }

    if (!formData.email.trim()) {
      newErrors.email = t.register.errors.email.required;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t.register.errors.email.invalid;
    }

    if (!formData.password) {
      newErrors.password = t.register.errors.password.required;
    } else if (formData.password.length < 6) {
      newErrors.password = t.register.errors.password.length;
    }

    if (formData.password !== confirmPassword) {
      newErrors.confirmPassword = t.register.errors.confirmPassword;
    }

    if (formData.image && formData.image.size > 15 * 1024 * 1024) {
      newErrors.image = t.register.errors.image;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    const form = new FormData();

    Object.entries(formData).forEach(([key, value]) => {
      if (key !== "image") form.append(key, value)
    })

    if (formData.image) {
      form.append("avatar", formData.image);
    }

    try {
      const data = await fetchRegisterUser(form);

      // Сохранение токена и роли в localStorage
      localStorage.setItem("token", data.token);
      localStorage.setItem("role", data.user.role);
      localStorage.setItem("userId", data.user.id);
      localStorage.setItem("firstName", data.user.firstName);
      localStorage.setItem("lastName", data.user.lastName);
      localStorage.setItem("libraryCard", data.user.libraryCard);

      // Авторизация в контексте
      login(data.token, data.user);

      // Показываем сообщение об успешной регистрации
      alert(
        `${t.register.success.title}\n\n${t.register.success.libraryCard} ${data.user.libraryCard}\n\n${t.register.success.welcome} ${data.user.firstName} ${data.user.lastName}!`
      );

      navigate("/");
    } catch (error) {
      console.error("Registration failed:", error);
      setErrors({ general: t.register.errors.general });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {t.register.title}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {t.register.subtitle}
          </p>
        </div>

        <form
          className="mt-8 space-y-6 bg-white p-8 rounded-lg shadow-md"
          onSubmit={handleSubmit}
        >
          {errors.general && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {errors.general}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Фамилия */}
            <div>
              <label
                htmlFor="lastName"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t.register.form.lastName} *
              </label>
              <input
                id="lastName"
                name="lastName"
                type="text"
                value={formData.lastName}
                onChange={handleChange}
                disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] focus:border-[var(--primary-color)]"
                placeholder={t.register.placeholders.lastName}
              />
              {errors.lastName && (
                <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
              )}
            </div>

            {/* Имя */}
            <div>
              <label
                htmlFor="firstName"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t.register.form.firstName} *
              </label>
              <input
                id="firstName"
                name="firstName"
                type="text"
                value={formData.firstName}
                onChange={handleChange}
                disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] focus:border-[var(--primary-color)]"
                placeholder={t.register.placeholders.firstName}
              />
              {errors.firstName && (
                <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
              )}
            </div>

            {/* Отчество */}
            <div>
              <label
                htmlFor="middleName"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t.register.form.middleName}
              </label>
              <input
                id="middleName"
                name="middleName"
                type="text"
                value={formData.middleName}
                onChange={handleChange}
                disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] focus:border-[var(--primary-color)]"
                placeholder={t.register.placeholders.middleName}
              />
            </div>

            {/* Пол */}
            <div>
              <label
                htmlFor="gender"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t.register.form.gender}
              </label>
              <select
                id="gender"
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] focus:border-[var(--primary-color)]"
              >
                {genderOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Год рождения */}
            <div>
              <label
                htmlFor="birthYear"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t.register.form.birthYear} *
              </label>
              <input
                id="birthYear"
                name="birthYear"
                type="number"
                min="1900"
                max={new Date().getFullYear()}
                value={formData.birthYear}
                onChange={handleChange}
                disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] focus:border-[var(--primary-color)]"
                placeholder={t.register.placeholders.birthYear}
              />
              {errors.birthYear && (
                <p className="mt-1 text-sm text-red-600">{errors.birthYear}</p>
              )}
            </div>

            {/* Национальность */}
            <div>
              <label
                htmlFor="nationality"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t.register.form.nationality}
              </label>
              <select
                id="nationality"
                name="nationality"
                value={formData.nationality}
                onChange={handleChange}
                disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] focus:border-[var(--primary-color)]"
              >
                <option value="">
                  {t.register.options.nationality.select}
                </option>
                {nationalityOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Адрес проживания */}
          <div>
            <label
              htmlFor="address"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              {t.register.form.address} *
            </label>
            <textarea
              id="address"
              name="address"
              rows="2"
              value={formData.address}
              onChange={handleChange}
              disabled={loading}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] focus:border-[var(--primary-color)]"
              placeholder={t.register.placeholders.address}
            />
            {errors.address && (
              <p className="mt-1 text-sm text-red-600">{errors.address}</p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Образование */}
            <div>
              <label
                htmlFor="education"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t.register.form.education} *
              </label>
              <select
                id="education"
                name="education"
                value={formData.education}
                onChange={handleChange}
                disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] focus:border-[var(--primary-color)]"
              >
                {educationOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Социальный статус */}
            <div>
              <label
                htmlFor="socialStatus"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t.register.form.socialStatus} *
              </label>
              <select
                id="socialStatus"
                name="socialStatus"
                value={formData.socialStatus}
                onChange={handleChange}
                disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] focus:border-[var(--primary-color)]"
              >
                {socialStatusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Место работы */}
            <div>
              <label
                htmlFor="workplace"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t.register.form.workplace}
              </label>
              <input
                id="workplace"
                name="workplace"
                type="text"
                value={formData.workplace}
                onChange={handleChange}
                disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] focus:border-[var(--primary-color)]"
                placeholder={t.register.placeholders.workplace}
              />
            </div>

            {/* Научная степень */}
            <div>
              <label
                htmlFor="academicDegree"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t.register.form.academicDegree}
              </label>
              <input
                id="academicDegree"
                name="academicDegree"
                type="text"
                value={formData.academicDegree}
                onChange={handleChange}
                disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] focus:border-[var(--primary-color)]"
                placeholder={t.register.placeholders.academicDegree}
              />
            </div>

            {/* Место учебы */}
            <div className="md:col-span-2">
              <label
                htmlFor="studyPlace"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t.register.form.studyPlace}
              </label>
              <input
                id="studyPlace"
                name="studyPlace"
                type="text"
                value={formData.studyPlace}
                onChange={handleChange}
                disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] focus:border-[var(--primary-color)]"
                placeholder={t.register.placeholders.studyPlace}
              />
            </div>

            {/* Email */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t.common.email} *
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] focus:border-[var(--primary-color)]"
                placeholder={t.register.placeholders.email}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            {/* Пароль */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t.common.password} *
              </label>
              <input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] focus:border-[var(--primary-color)]"
                placeholder={t.register.placeholders.password}
              />
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password}</p>
              )}
            </div>

            {/* Подтверждение пароля */}
            <div className="md:col-span-2">
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t.register.form.confirmPassword} *
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (errors.confirmPassword) {
                    setErrors((prev) => ({ ...prev, confirmPassword: "" }));
                  }
                }}
                disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] focus:border-[var(--primary-color)]"
                placeholder={t.register.placeholders.confirmPassword}
              />
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.confirmPassword}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="avatar"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t.register.form.uploadAvatar} *
              </label>
              <input
                id="avatar"
                name="avatar"
                type="file"
                accept="image/*"
                onChange={(e) => setFormData({ ...formData, image: e.target.files[0] })}
                disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] focus:border-[var(--primary-color)]"
              />

              {errors.image && (
                <p className="mt-1 text-sm text-red-600">{errors.image}</p>
              )}
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-[var(--primary-color)] hover:bg-white hover:text-black hover:border-[var(--primary-color)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--primary-color)] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              {loading
                ? t.register.buttons.registering
                : t.register.buttons.register}
            </button>
          </div>

          <div className="text-center">
            <p className="text-sm text-gray-600">
              {t.register.footer.hasAccount}{" "}
              <a
                href="/login"
                className="font-medium text-[var(--primary-color)] hover:text-[var(--primary-color)]"
              >
                {t.register.footer.login}
              </a>
            </p>
          </div>
        </form>
      </div >
    </div >
  );
};

export default RegisterPage;
